import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './DeliveryTouchPage.css';
import {
    DeliveryOrder,
    OrderChannel,
    OrderChannelEnum,
    OrderStatus,
    OrderStatusEnum,
} from './ordersTypes';
import type { ApiOrderBoardOrder } from '../../api/ordersApi';
import {
    fetchOrdersBoard,
    normalizeApiStatus,
    normalizeChannel,
    normalizePaymentStatus,
    updateOrderStatus as apiUpdateOrderStatus,
} from '../../api/ordersApi';
import type { PrintDestinationDTO, PrintTemplateDTO } from '../../api/printApi';
import {
    listPrintDestinations,
    listPrintTemplates,
    manualReprintOrder,
} from '../../api/printApi';

const currencyFormat = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const formatCurrency = (value?: number | null) => currencyFormat.format(value || 0);

const formatTime = (dateString?: string | null) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

const formatDate = (dateString?: string | null) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        const today = new Date();
        const isToday = date.toDateString() === today.toDateString();

        if (isToday) {
            return formatTime(dateString);
        }
        return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
    } catch {
        return '';
    }
};

const isDateToday = (dateString?: string | null) => {
    if (!dateString) {
        return false;
    }
    try {
        const target = new Date(dateString);
        if (Number.isNaN(target.getTime())) {
            return false;
        }
        const now = new Date();
        return target.getFullYear() === now.getFullYear() &&
            target.getMonth() === now.getMonth() &&
            target.getDate() === now.getDate();
    } catch {
        return false;
    }
};

// Calcola minuti trascorsi dalla creazione
const getElapsedMinutes = (createdAt: string): number => {
    try {
        const created = new Date(createdAt);
        const now = new Date();
        return Math.floor((now.getTime() - created.getTime()) / 60000);
    } catch {
        return 0;
    }
};

const statusLabels: Record<OrderStatus, string> = {
    NEW: 'Nuovo',
    ACKNOWLEDGED: 'Confermato',
    PREPARING: 'In preparazione',
    READY: 'Pronto',
    ON_DELIVERY: 'In consegna',
    DELIVERED: 'Consegnato',
    CANCELLED: 'Annullato',
};

const channelIcons: Record<OrderChannel, string> = {
    DELIVERY: '🚴',
    TAKEAWAY: '📦',
    DINE_IN: '🍽️',
};

function mapOrderFromApi(apiOrder: ApiOrderBoardOrder): DeliveryOrder {
    const channel = normalizeChannel(apiOrder.channel) ?? OrderChannelEnum.DELIVERY;
    const status = normalizeApiStatus(apiOrder.status) ?? OrderStatusEnum.NEW;
    const paymentStatus = normalizePaymentStatus(apiOrder.paymentStatus) ?? 'UNPAID';
    const createdAt = apiOrder.createdAt ?? new Date().toISOString();

    const items = (apiOrder.items ?? []).map((item, index) => {
        const note = typeof item.note === 'string' && item.note.trim().length > 0 ? item.note.trim() : undefined;
        const modifiers = Array.isArray(item.modifiers)
            ? item.modifiers.filter(modifier => typeof modifier === 'string' && modifier.trim().length > 0)
            : undefined;

        return {
            id: item.id ?? `${apiOrder.id}-item-${index}`,
            name: item.name ?? 'Articolo',
            quantity: typeof item.quantity === 'number' ? item.quantity : 1,
            price: typeof item.price === 'number' ? item.price : 0,
            total: typeof item.total === 'number' ? item.total : (item.price ?? 0) * (item.quantity ?? 1),
            modifiers,
            category: item.category,
            station: item.station,
            note,
        };
    });

    const total = typeof apiOrder.total === 'number' ? apiOrder.total : items.reduce((acc, item) => acc + item.total, 0);

    return {
        id: `${channel.toLowerCase()}-${apiOrder.id}`,
        backendId: String(apiOrder.id),
        code: apiOrder.code ?? `${channel}-${apiOrder.id}`,
        createdAt,
        scheduledAt: apiOrder.scheduledAt ?? undefined,
        status,
        channel,
        paymentMethod: apiOrder.paymentMethod ?? 'Online',
        paymentStatus,
        total,
        serviceFee: typeof apiOrder.serviceFee === 'number' ? apiOrder.serviceFee : undefined,
        items,
        customer: {
            name: apiOrder.customer?.name ?? 'Cliente',
            phone: apiOrder.customer?.phone,
            email: apiOrder.customer?.email,
            address: apiOrder.customer?.address ? {
                line1: apiOrder.customer.address.line1 ?? '',
                line2: apiOrder.customer.address.line2,
                city: apiOrder.customer.address.city ?? '',
                postalCode: apiOrder.customer.address.postalCode ?? '',
            } : undefined,
        },
        notes: {
            customer: apiOrder.notes?.customer?.trim() || undefined,
            kitchen: apiOrder.notes?.kitchen?.trim() || undefined,
            delivery: apiOrder.notes?.delivery?.trim() || undefined,
            billing: apiOrder.notes?.billing?.trim() || undefined,
        },
        timeline: [],
        tags: apiOrder.tags ?? [],
        takeaway: apiOrder.takeaway ? {
            pickupAt: apiOrder.takeaway.pickupAt,
            pickupName: apiOrder.takeaway.pickupName,
        } : undefined,
    };
}

const DeliveryTouchPage: React.FC = () => {
    const [orders, setOrders] = useState<DeliveryOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<OrderStatus | 'ALL'>('ALL');
    const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
    const [soundEnabled, setSoundEnabled] = useState(() => {
        try {
            const stored = window.localStorage.getItem('delivery.soundEnabled');
            // Se mai impostato, default = true (attivo)
            return stored === null ? true : stored === '1';
        } catch {
            return true; // Default attivo anche se localStorage non disponibile
        }
    });

    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const ringOscillatorRef = useRef<OscillatorNode | null>(null);
    const ringGainRef = useRef<GainNode | null>(null);
    const alarmIntervalRef = useRef<number | null>(null);
    const alarmActiveRef = useRef(false);
    const knownOrderIdsRef = useRef<Set<string>>(new Set());
    const initialLoadRef = useRef(true);
    const autoPrintedOrdersRef = useRef<Set<string>>(new Set());

    // Stati per gestione stampa
    const [printDestinations, setPrintDestinations] = useState<PrintDestinationDTO[]>([]);
    const [printTemplates, setPrintTemplates] = useState<PrintTemplateDTO[]>([]);
    const [printConfigLoaded, setPrintConfigLoaded] = useState(false);

    // Modal stampa ricevuta
    const [receiptModalOpen, setReceiptModalOpen] = useState(false);
    const [receiptModalOrder, setReceiptModalOrder] = useState<DeliveryOrder | null>(null);
    const [receiptPrinting, setReceiptPrinting] = useState(false);

    const deliveryOrders = useMemo(() => {
        // Filtra SOLO delivery e takeaway
        return orders.filter(order =>
            order.channel === OrderChannelEnum.DELIVERY ||
            order.channel === OrderChannelEnum.TAKEAWAY
        );
    }, [orders]);

    const filteredOrders = useMemo(() => {
        return deliveryOrders.filter(order => {
            if (statusFilter === 'ALL') return true;
            return order.status === statusFilter;
        });
    }, [deliveryOrders, statusFilter]);

    const statusCounts = useMemo(() => {
        const counts: Record<OrderStatus, number> = {
            NEW: 0,
            ACKNOWLEDGED: 0,
            PREPARING: 0,
            READY: 0,
            ON_DELIVERY: 0,
            DELIVERED: 0,
            CANCELLED: 0,
        };
        deliveryOrders.forEach(order => {
            counts[order.status] = (counts[order.status] || 0) + 1;
        });
        return counts;
    }, [deliveryOrders]);

    const hasNewOrders = useMemo(() => {
        return deliveryOrders.some(order => order.status === OrderStatusEnum.NEW);
    }, [deliveryOrders]);

    const ensureAudioContext = useCallback(async () => {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) {
            console.warn('Web Audio API not available');
            return null;
        }

        if (!audioContextRef.current) {
            audioContextRef.current = new AudioContextClass();
        }

        if (audioContextRef.current.state === 'suspended') {
            try {
                await audioContextRef.current.resume();
            } catch (resumeError) {
                console.warn('Failed to resume audio context', resumeError);
            }
        }

        return audioContextRef.current;
    }, []);

    const startAlarm = useCallback(async () => {
        if (!soundEnabled) {
            return;
        }
        if (alarmActiveRef.current) {
            const ctx = await ensureAudioContext();
            if (ctx && ctx.state === 'suspended') {
                try {
                    await ctx.resume();
                } catch (resumeError) {
                    console.warn('Unable to resume audio context', resumeError);
                }
            }
            return;
        }

        const ctx = await ensureAudioContext();
        if (!ctx) {
            return;
        }

        const oscillator = ctx.createOscillator();
        oscillator.type = 'triangle';
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);

        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.start();

        ringOscillatorRef.current = oscillator;
        ringGainRef.current = gain;
        alarmActiveRef.current = true;

        const scheduleCycle = () => {
            if (!audioContextRef.current || !ringOscillatorRef.current || !ringGainRef.current) {
                return;
            }
            const now = audioContextRef.current.currentTime;
            const osc = ringOscillatorRef.current;
            const oscGain = ringGainRef.current;

            osc.frequency.cancelScheduledValues(now);
            oscGain.gain.cancelScheduledValues(now);

            osc.frequency.setValueAtTime(880, now);
            oscGain.gain.setValueAtTime(0.0001, now);
            oscGain.gain.exponentialRampToValueAtTime(0.28, now + 0.02);
            oscGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);

            osc.frequency.setValueAtTime(660, now + 0.35);
            oscGain.gain.setValueAtTime(0.0001, now + 0.35);
            oscGain.gain.exponentialRampToValueAtTime(0.32, now + 0.37);
            oscGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.62);
        };

        scheduleCycle();
        if (alarmIntervalRef.current !== null) {
            window.clearInterval(alarmIntervalRef.current);
        }
        alarmIntervalRef.current = window.setInterval(scheduleCycle, 900);
    }, [ensureAudioContext, soundEnabled]);

    const stopAlarm = useCallback(() => {
        if (alarmIntervalRef.current !== null) {
            window.clearInterval(alarmIntervalRef.current);
            alarmIntervalRef.current = null;
        }

        const ctx = audioContextRef.current;
        const gain = ringGainRef.current;
        const oscillator = ringOscillatorRef.current;

        if (ctx && gain) {
            const now = ctx.currentTime;
            gain.gain.cancelScheduledValues(now);
            gain.gain.setValueAtTime(Math.max(gain.gain.value, 0.0001), now);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
        }

        if (oscillator) {
            const stopAt = (ctx?.currentTime ?? 0) + 0.25;
            try {
                oscillator.stop(stopAt);
            } catch (err) {
                try {
                    oscillator.stop();
                } catch {
                    // Oscillator might already be stopped.
                }
            }
            oscillator.onended = () => {
                if (ringOscillatorRef.current === oscillator) {
                    ringOscillatorRef.current = null;
                }
                if (ringGainRef.current === gain) {
                    ringGainRef.current = null;
                }
            };
        } else {
            ringGainRef.current = null;
        }

        alarmActiveRef.current = false;
    }, []);

    const playNotificationSound = useCallback(async (forcePreview = false) => {
        if (!soundEnabled && !forcePreview) {
            return;
        }

        const ctx = await ensureAudioContext();
        if (!ctx) {
            return;
        }

        const startAt = ctx.currentTime;

        const beep = (offset: number, freq: number) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, startAt + offset);
            gain.gain.setValueAtTime(0.0001, startAt + offset);
            gain.gain.exponentialRampToValueAtTime(0.35, startAt + offset + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, startAt + offset + 0.28);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(startAt + offset);
            osc.stop(startAt + offset + 0.32);
        };

        beep(0, 880);
        beep(0.35, 660);
    }, [ensureAudioContext, soundEnabled]);

    useEffect(() => {
        if (soundEnabled && hasNewOrders) {
            void startAlarm();
        } else {
            stopAlarm();
        }
    }, [soundEnabled, hasNewOrders, startAlarm, stopAlarm]);

    const loadOrders = useCallback(async () => {
        try {
            const response = await fetchOrdersBoard();
            const mapped = response.orders.map(mapOrderFromApi);
            const todaysOrders = mapped.filter(order =>
                // Keep only orders that belong to the current business day so the board resets overnight.
                isDateToday(order.createdAt) ||
                isDateToday(order.scheduledAt)
            );

            // Notifica solo per nuovi ordini delivery/takeaway
            const deliveryMapped = todaysOrders.filter(o =>
                o.channel === OrderChannelEnum.DELIVERY ||
                o.channel === OrderChannelEnum.TAKEAWAY
            );

            if (!initialLoadRef.current) {
                const currentIds = new Set(deliveryMapped.map(o => o.id));
                const newOrders = deliveryMapped.filter(o =>
                    !knownOrderIdsRef.current.has(o.id) &&
                    o.status === OrderStatusEnum.NEW
                );

                if (newOrders.length > 0) {
                    void playNotificationSound();
                }

                knownOrderIdsRef.current = currentIds;
            } else {
                knownOrderIdsRef.current = new Set(deliveryMapped.map(o => o.id));
                initialLoadRef.current = false;
            }

            setOrders(todaysOrders);
            setError(null);
        } catch (err) {
            console.error('Error loading orders:', err);
            setError(err instanceof Error ? err.message : 'Errore caricamento ordini');
        } finally {
            setLoading(false);
        }
    }, [playNotificationSound]);

    useEffect(() => {
        loadOrders();

        pollingRef.current = setInterval(() => {
            loadOrders();
        }, 15000); // Aggiorna ogni 15 secondi

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
            stopAlarm();
            if (audioContextRef.current) {
                audioContextRef.current.close().catch(() => undefined);
                audioContextRef.current = null;
            }
            ringOscillatorRef.current = null;
            ringGainRef.current = null;
        };
    }, [loadOrders, stopAlarm]);

    // Carica configurazione stampanti
    useEffect(() => {
        const loadPrintConfig = async () => {
            try {
                const [destinations, templates] = await Promise.all([
                    listPrintDestinations(),
                    listPrintTemplates(),
                ]);
                setPrintDestinations(destinations);
                setPrintTemplates(templates);
                setPrintConfigLoaded(true);
            } catch (err) {
                console.warn('Print config not available:', err);
                setPrintConfigLoaded(true); // Segna come caricato anche se fallito
            }
        };
        loadPrintConfig();
    }, []);

    // Auto-stampa ordini nuovi (BUSINESS template)
    useEffect(() => {
        if (!printConfigLoaded || deliveryOrders.length === 0) return;

        const newOrders = deliveryOrders.filter(order =>
            order.status === OrderStatusEnum.NEW &&
            !autoPrintedOrdersRef.current.has(order.id)
        );

        if (newOrders.length === 0) return;

        // Trova template BUSINESS attivo
        const businessTemplate = printTemplates.find(
            t => t.templateType === 'BUSINESS' && t.active
        );

        // Trova destinazione BUSINESS abilitata
        const businessDestination = printDestinations.find(
            d => d.destinationType === 'BUSINESS' && d.enabled
        );

        if (!businessTemplate || !businessDestination) {
            console.log('Auto-print skipped: no BUSINESS template or destination configured');
            return;
        }

        // Stampa ogni nuovo ordine
        newOrders.forEach(async (order) => {
            try {
                autoPrintedOrdersRef.current.add(order.id);

                await manualReprintOrder(order.backendId, {
                    destinationId: businessDestination.id,
                    templateId: businessTemplate.id,
                    channel: order.channel,
                });

                console.log(`Auto-printed order ${order.code} (BUSINESS)`);
            } catch (err) {
                console.error(`Auto-print failed for order ${order.code}:`, err);
                // Rimuovi dall'elenco stampati per ritentare
                autoPrintedOrdersRef.current.delete(order.id);
            }
        });
    }, [deliveryOrders, printConfigLoaded, printDestinations, printTemplates]);

    const handleStatusChange = useCallback(async (order: DeliveryOrder, newStatus: OrderStatus) => {
        // Intercetta passaggio a READY per mostrare modal stampa ricevuta
        if (newStatus === OrderStatusEnum.READY && order.status !== OrderStatusEnum.READY) {
            setReceiptModalOrder(order);
            setReceiptModalOpen(true);
            return; // Non cambiare stato ancora
        }

        try {
            await apiUpdateOrderStatus(order.channel, order.backendId, newStatus);
            await loadOrders();
        } catch (err) {
            console.error('Error updating status:', err);
            alert('Errore durante l\'aggiornamento dello stato');
        }
    }, [loadOrders]);

    const handleConfirmReady = useCallback(async (printReceipt: boolean) => {
        if (!receiptModalOrder) return;

        try {
            // Se richiesta stampa ricevuta
            if (printReceipt) {
                const receiptTemplate = printTemplates.find(
                    t => t.templateType === 'RECEIPT' && t.active
                );
                const receiptDestination = printDestinations.find(
                    d => d.destinationType === 'RECEIPT' && d.enabled
                );

                if (receiptTemplate && receiptDestination) {
                    setReceiptPrinting(true);
                    try {
                        await manualReprintOrder(receiptModalOrder.backendId, {
                            destinationId: receiptDestination.id,
                            templateId: receiptTemplate.id,
                            channel: receiptModalOrder.channel,
                        });
                        console.log(`Receipt printed for order ${receiptModalOrder.code}`);
                    } catch (err) {
                        console.error('Receipt print failed:', err);
                        alert('Errore durante la stampa della ricevuta. Lo stato verrà comunque aggiornato.');
                    } finally {
                        setReceiptPrinting(false);
                    }
                } else {
                    alert('Stampante ricevute non configurata. Configura nel Centro Stampa.');
                }
            }

            // Aggiorna stato a READY
            await apiUpdateOrderStatus(receiptModalOrder.channel, receiptModalOrder.backendId, OrderStatusEnum.READY);
            await loadOrders();

            setReceiptModalOpen(false);
            setReceiptModalOrder(null);
        } catch (err) {
            console.error('Error updating to READY:', err);
            alert('Errore durante l\'aggiornamento dello stato');
        }
    }, [receiptModalOrder, printTemplates, printDestinations, loadOrders]);

    const handleCancelReady = useCallback(() => {
        setReceiptModalOpen(false);
        setReceiptModalOrder(null);
    }, []);

    const toggleSound = useCallback(() => {
        const next = !soundEnabled;
        setSoundEnabled(next);
        try {
            window.localStorage.setItem('delivery.soundEnabled', next ? '1' : '0');
        } catch {
            // Ignore storage errors
        }

        if (next) {
            if (hasNewOrders) {
                void startAlarm();
            } else {
                void playNotificationSound(true);
            }
        } else {
            stopAlarm();
        }
    }, [soundEnabled, hasNewOrders, startAlarm, stopAlarm, playNotificationSound]);

    const getTimerColor = (minutes: number): string => {
        if (minutes < 15) return 'green';
        if (minutes < 30) return 'yellow';
        return 'red';
    };

    const getNextStatus = (currentStatus: OrderStatus, channel: OrderChannel): OrderStatus | null => {
        switch (currentStatus) {
            case OrderStatusEnum.NEW:
                return OrderStatusEnum.ACKNOWLEDGED;
            case OrderStatusEnum.ACKNOWLEDGED:
                return OrderStatusEnum.PREPARING;
            case OrderStatusEnum.PREPARING:
                return OrderStatusEnum.READY;
            case OrderStatusEnum.READY:
                return channel === OrderChannelEnum.DELIVERY
                    ? OrderStatusEnum.ON_DELIVERY
                    : OrderStatusEnum.DELIVERED;
            case OrderStatusEnum.ON_DELIVERY:
                return OrderStatusEnum.DELIVERED;
            default:
                return null;
        }
    };

    const getNextStatusLabel = (currentStatus: OrderStatus, channel: OrderChannel): string => {
        const next = getNextStatus(currentStatus, channel);
        if (!next) return '';

        switch (next) {
            case OrderStatusEnum.ACKNOWLEDGED: return '✓ Accetta';
            case OrderStatusEnum.PREPARING: return '🍳 In Preparazione';
            case OrderStatusEnum.READY: return '✓ Pronto';
            case OrderStatusEnum.ON_DELIVERY: return '🚴 In Consegna';
            case OrderStatusEnum.DELIVERED: return '✓ Consegnato';
            default: return statusLabels[next];
        }
    };

    if (loading && orders.length === 0) {
        return (
            <div className="delivery-touch-page">
                <div className="delivery-touch-loading">Caricamento ordini...</div>
            </div>
        );
    }

    return (
        <div className="delivery-touch-page">
            {/* Header */}
            <header className="delivery-touch-header">
                <div className="delivery-touch-title">
                    <h1>🚴 Delivery & Asporto</h1>
                    <span className="delivery-touch-count">{deliveryOrders.length} ordini</span>
                </div>
                <div className="delivery-touch-actions">
                    <button
                        className={`btn-sound ${soundEnabled ? 'active' : ''}`}
                        onClick={toggleSound}
                        title={soundEnabled ? 'Disattiva audio' : 'Attiva audio'}
                    >
                        {soundEnabled ? '🔔' : '🔕'}
                    </button>
                    <button className="btn-refresh" onClick={loadOrders} title="Aggiorna">
                        ↻
                    </button>
                </div>
            </header>

            {/* Status Tabs */}
            <div className="delivery-touch-tabs">
                <button
                    className={`tab-btn ${statusFilter === 'ALL' ? 'active' : ''}`}
                    onClick={() => setStatusFilter('ALL')}
                >
                    <span className="tab-icon">📋</span>
                    <span className="tab-label">Tutti</span>
                    <span className="tab-count">{deliveryOrders.length}</span>
                </button>
                <button
                    className={`tab-btn ${statusFilter === OrderStatusEnum.NEW ? 'active' : ''} ${statusCounts.NEW > 0 ? 'has-items' : ''}`}
                    onClick={() => setStatusFilter(OrderStatusEnum.NEW)}
                >
                    <span className="tab-icon">🔔</span>
                    <span className="tab-label">Nuovi</span>
                    <span className="tab-count">{statusCounts.NEW}</span>
                </button>
                <button
                    className={`tab-btn ${statusFilter === OrderStatusEnum.PREPARING ? 'active' : ''}`}
                    onClick={() => setStatusFilter(OrderStatusEnum.PREPARING)}
                >
                    <span className="tab-icon">🍳</span>
                    <span className="tab-label">Prep</span>
                    <span className="tab-count">{statusCounts.PREPARING}</span>
                </button>
                <button
                    className={`tab-btn ${statusFilter === OrderStatusEnum.READY ? 'active' : ''}`}
                    onClick={() => setStatusFilter(OrderStatusEnum.READY)}
                >
                    <span className="tab-icon">✓</span>
                    <span className="tab-label">Pronti</span>
                    <span className="tab-count">{statusCounts.READY}</span>
                </button>
                <button
                    className={`tab-btn ${statusFilter === OrderStatusEnum.ON_DELIVERY ? 'active' : ''}`}
                    onClick={() => setStatusFilter(OrderStatusEnum.ON_DELIVERY)}
                >
                    <span className="tab-icon">🚴</span>
                    <span className="tab-label">Consegna</span>
                    <span className="tab-count">{statusCounts.ON_DELIVERY}</span>
                </button>
            </div>

            {/* Orders List */}
            <div className="delivery-touch-content">
                {error && (
                    <div className="delivery-touch-error">
                        {error}
                        <button onClick={loadOrders}>Riprova</button>
                    </div>
                )}

                {!error && filteredOrders.length === 0 && (
                    <div className="delivery-touch-empty">
                        Nessun ordine in questo stato
                    </div>
                )}

                <div className="delivery-orders-list">
                    {filteredOrders.map(order => {
                        const isExpanded = expandedOrderId === order.id;
                        const elapsed = getElapsedMinutes(order.createdAt);
                        const timerColor = getTimerColor(elapsed);
                        const nextStatus = getNextStatus(order.status, order.channel);
                        const nextLabel = getNextStatusLabel(order.status, order.channel);

                        return (
                            <div
                                key={order.id}
                                className={`delivery-order-card ${isExpanded ? 'expanded' : ''} timer-${timerColor}`}
                            >
                                {/* Card Header - Always Visible */}
                                <div
                                    className="delivery-order-header"
                                    onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                                >
                                    <div className="delivery-order-top">
                                        <div className="delivery-order-info">
                                            <strong className="delivery-order-code">
                                                {channelIcons[order.channel]} {order.code}
                                            </strong>
                                            <span className="delivery-order-time">
                                                ⏱️ {elapsed}min · {formatTime(order.createdAt)}
                                            </span>
                                        </div>
                                        <span className="delivery-order-total">{formatCurrency(order.total)}</span>
                                    </div>

                                    <div className="delivery-order-customer">
                                        <span className="customer-name">{order.customer.name}</span>
                                        {order.customer.phone && (
                                            <a
                                                href={`tel:${order.customer.phone}`}
                                                className="customer-phone"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                📞 {order.customer.phone}
                                            </a>
                                        )}
                                    </div>

                                    {order.channel === OrderChannelEnum.DELIVERY && order.customer.address && (
                                        <div className="delivery-order-address">
                                            📍 {order.customer.address.line1}
                                            {order.customer.address.city && `, ${order.customer.address.city}`}
                                        </div>
                                    )}

                                    {order.channel === OrderChannelEnum.TAKEAWAY && order.takeaway?.pickupAt && (
                                        <div className="delivery-order-pickup">
                                            🕐 Ritiro: {formatTime(order.takeaway.pickupAt)}
                                            {order.takeaway.pickupName && ` · ${order.takeaway.pickupName}`}
                                        </div>
                                    )}

                                    <div className="delivery-order-meta">
                                        <span className="item-count">{order.items.length} articoli</span>
                                        <span className={`payment-badge ${order.paymentStatus === 'PAID' ? 'paid' : ''}`}>
                                            {order.paymentStatus === 'PAID' ? '💳 Pagato' : '💵 Da incassare'}
                                        </span>
                                    </div>
                                </div>

                                {/* Expanded Details */}
                                {isExpanded && (
                                    <div className="delivery-order-details">
                                        <div className="delivery-order-items">
                                            <h4>Prodotti:</h4>
                                            {order.items.map((item, idx) => (
                                                <div key={item.id ?? idx} className="delivery-item">
                                                    <span className="item-quantity">x{item.quantity}</span>
                                                    <div className="item-main">
                                                        <span className="item-name">{item.name}</span>
                                                        {item.modifiers && item.modifiers.length > 0 && (
                                                            <ul className="item-modifiers">
                                                                {item.modifiers.map(modifier => (
                                                                    <li key={modifier}>{modifier}</li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        {item.note && (
                                                            <p className="item-note">{item.note}</p>
                                                        )}
                                                    </div>
                                                    <span className="item-price">{formatCurrency(item.total)}</span>
                                                </div>
                                            ))}
                                        </div>

                                        {(order.notes?.customer || order.notes?.kitchen || order.notes?.delivery) && (
                                            <div className="delivery-order-notes">
                                                <strong>Note ordine</strong>
                                                {order.notes?.customer && (
                                                    <p><span className="note-label">Cliente:</span> {order.notes.customer}</p>
                                                )}
                                                {order.notes?.kitchen && (
                                                    <p><span className="note-label">Cucina:</span> {order.notes.kitchen}</p>
                                                )}
                                                {order.notes?.delivery && (
                                                    <p><span className="note-label">Consegna:</span> {order.notes.delivery}</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Action Buttons */}
                                        <div className="delivery-order-actions">
                                            {nextStatus && (
                                                <button
                                                    className="btn-next-status"
                                                    onClick={() => handleStatusChange(order, nextStatus)}
                                                >
                                                    {nextLabel}
                                                </button>
                                            )}
                                            {order.status !== OrderStatusEnum.CANCELLED &&
                                                order.status !== OrderStatusEnum.DELIVERED && (
                                                    <button
                                                        className="btn-cancel"
                                                        onClick={() => {
                                                            if (confirm('Annullare questo ordine?')) {
                                                                handleStatusChange(order, OrderStatusEnum.CANCELLED);
                                                            }
                                                        }}
                                                    >
                                                        ❌ Annulla
                                                    </button>
                                                )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal Stampa Ricevuta */}
            {receiptModalOpen && receiptModalOrder && (
                <div className="delivery-modal-overlay" onClick={handleCancelReady}>
                    <div className="delivery-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="delivery-modal-header">
                            <h3>🖨️ Ordine Pronto</h3>
                            <button className="delivery-modal-close" onClick={handleCancelReady}>×</button>
                        </div>

                        <div className="delivery-modal-body">
                            <p className="delivery-modal-order-info">
                                <strong>{receiptModalOrder.code}</strong> - {receiptModalOrder.customer.name}
                            </p>

                            {receiptModalOrder.paymentStatus === 'PAID' ? (
                                <div className="delivery-modal-paid">
                                    <p>✅ <strong>Ordine già pagato online</strong></p>
                                    <p>Vuoi stampare la ricevuta comunque?</p>
                                </div>
                            ) : (
                                <div className="delivery-modal-unpaid">
                                    <p>💵 <strong>Pagamento da incassare</strong></p>
                                    <p>Metodo: {receiptModalOrder.paymentMethod || 'Non specificato'}</p>
                                    <p>Importo: {formatCurrency(receiptModalOrder.total)}</p>
                                    <p className="delivery-modal-hint">
                                        Stampa la ricevuta per il cliente
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="delivery-modal-actions">
                            <button
                                className="btn-modal-print"
                                onClick={() => handleConfirmReady(true)}
                                disabled={receiptPrinting}
                            >
                                {receiptPrinting ? '🖨️ Stampa in corso...' : '✓ Sì, stampa ricevuta'}
                            </button>
                            <button
                                className="btn-modal-skip"
                                onClick={() => handleConfirmReady(false)}
                                disabled={receiptPrinting}
                            >
                                ⏭️ No, salta
                            </button>
                            <button
                                className="btn-modal-cancel"
                                onClick={handleCancelReady}
                                disabled={receiptPrinting}
                            >
                                ← Annulla
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeliveryTouchPage;
